import { computed, reactive, ref } from 'vue';
import { defineStore } from 'pinia';
import { http } from '@/services/http';
import { getActiveFilterTemplateId, setActiveFilterTemplateId } from '@/services/filter-template';

export interface ToastMessage {
  id: number;
  tone: 'success' | 'error' | 'info';
  text: string;
}

/** 本地测试通过门禁状态（仅本地环境显示按钮，生产 env=prod 自动隐藏） */
export interface TestPassState {
  env: string;
  pass: boolean;
  loading: boolean;
  msg: string;
}

/**
 * 1.1.78: 全局筛选（筛选模板）选项 —— 在「权限管理 → 筛选模板」里配置，
 * 顶栏选中后全站只看该模板的 SPU 范围（与本人权限取交集，只窄不宽）。
 */
export interface FilterTemplateOption {
  id: number;
  name: string;
  description?: string | null;
  spu_count: number;
  source_department_id?: string | null;
}

/** 侧边栏收起状态持久化键（刷新后保持用户选择） */
const SIDEBAR_KEY = 'sku_sidebar_collapsed';

/** 切换筛选后要提示的文案（写 sessionStorage，刷新后取一次即清） */
const FILTER_TOAST_KEY = 'sku_filter_toast';

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false; // 隐私模式等 localStorage 不可用
  }
}

export const useAppStore = defineStore('app', () => {
  const sidebarCollapsed = ref(readSidebarCollapsed());
  const version = ref('');
  const toasts = ref<ToastMessage[]>([]);
  const testPass = reactive<TestPassState>({ env: 'prod', pass: false, loading: false, msg: '' });
  let toastId = 0;

  const versionLabel = computed(() => (version.value ? `v${version.value}` : ''));

  /* ===== 1.1.78: 全局筛选（筛选模板）===== */
  const filterTemplates = ref<FilterTemplateOption[]>([]);
  const activeFilterTemplateId = ref<number | null>(getActiveFilterTemplateId());
  const activeFilterTemplate = computed(
    () => filterTemplates.value.find((item) => item.id === activeFilterTemplateId.value) || null,
  );

  /** 拉取可选模板（顶栏下拉用）。失败就静默清空 —— 它只是便利功能，不该挡住页面。 */
  async function loadFilterTemplates(): Promise<void> {
    try {
      const data = await http.get<{ rows?: FilterTemplateOption[] }>('/api/auth/permissions/filter-templates');
      filterTemplates.value = Array.isArray(data.rows) ? data.rows : [];
    } catch {
      filterTemplates.value = [];
      return;
    }
    // 之前选的模板被删掉了 → 自动清空，避免一直带着一个不存在的筛选
    if (activeFilterTemplateId.value && !filterTemplates.value.some((item) => item.id === activeFilterTemplateId.value)) {
      setFilterTemplate(null);
    }
  }

  /** 切换中：给一层过渡遮罩，避免"咔"一下硬刷新（见 AppShell 里的 filter-curtain） */
  const filterApplying = ref(false);

  /**
   * 切换全局筛选。
   * 仍需整页刷新（各页面/资源 store 都缓存着上一份数据，逐个通知既容易漏又要改一堆页面），
   * 但做了三点体验处理：① 先亮起过渡遮罩再刷（不是硬刷新）；
   * ② 刷新后弹一条成功提示，明确告知当前范围；③ 库里没名称时给通用文案。
   */
  function setFilterTemplate(id: number | null): void {
    setActiveFilterTemplateId(id);
    activeFilterTemplateId.value = getActiveFilterTemplateId();
    const picked = filterTemplates.value.find((item) => item.id === activeFilterTemplateId.value) || null;
    try {
      sessionStorage.setItem(
        FILTER_TOAST_KEY,
        picked ? `已应用筛选：只看「${picked.name}」（${picked.spu_count} 个 SPU）` : id ? '已应用筛选' : '已恢复「全部数据」范围',
      );
    } catch {
      /* 隐私模式忽略 */
    }
    filterApplying.value = true;
    window.setTimeout(() => window.location.reload(), 340);
  }

  /** 刷新后取一次提示（取完即清，避免每次刷新都弹） */
  function consumeFilterToast(): string {
    try {
      const text = sessionStorage.getItem(FILTER_TOAST_KEY) || '';
      if (text) sessionStorage.removeItem(FILTER_TOAST_KEY);
      return text;
    } catch {
      return '';
    }
  }

  /** 收起 / 展开左侧导航（状态写入 localStorage，刷新后保持） */
  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    try {
      localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed.value ? '1' : '0');
    } catch {
      /* 忽略写入失败 */
    }
  }

  /* 1.1.120: 本地开发自检 —— 后端"跑的是旧代码"时页面顶部会亮红条 ✓
     （用户反复反馈"改了后端代码、页面数字不变"✗，根因都是本地进程没重启 ✗） */
  const backendEnv = ref<'local' | 'prod'>('prod');
  const backendStale = ref(false);
  const backendStartedAt = ref('');
  const backendSourceAt = ref('');
  const backendSha = ref('');
  const backendPid = ref(0);

  async function loadVersion(): Promise<void> {
    try {
      const data = await http.get<{
        version: string;
        sha?: string;
        env?: 'local' | 'prod';
        started_at?: string;
        pid?: number;
        source_newest_at?: string | null;
        stale?: boolean;
      }>('/api/version');
      version.value = data.version;
      backendEnv.value = data.env === 'local' ? 'local' : 'prod';
      backendStale.value = Boolean(data.stale);
      backendStartedAt.value = data.started_at || '';
      backendSourceAt.value = data.source_newest_at || '';
      backendSha.value = data.sha || '';
      backendPid.value = Number(data.pid) || 0;
    } catch {
      version.value = '';
    }
  }

  function notify(text: string, tone: ToastMessage['tone'] = 'info'): void {
    const id = ++toastId;
    toasts.value.push({ id, text, tone });
    window.setTimeout(() => dismiss(id), 3600);
  }

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((toast) => toast.id !== id);
  }

  /** 查询门禁状态（带时间戳绕开 GET 合并缓存，等价于旧实现的 cache: no-store） */
  async function checkTestPass(): Promise<void> {
    try {
      const data = await http.get<{ env?: string; pass?: boolean; msg?: string }>(
        `/api/test_pass/check?t=${Date.now()}`,
      );
      testPass.env = data.env || 'prod';
      testPass.pass = Boolean(data.pass);
      testPass.msg = data.msg || '';
    } catch {
      testPass.env = 'prod';
    }
  }

  /** 页面按钮：记录测试通过（source=ui，pre-push 钩子只认这个来源） */
  async function markTestPass(): Promise<void> {
    testPass.loading = true;
    try {
      const data = await http.post<{ ok?: boolean; msg?: string }>('/api/test_pass', { source: 'ui' });
      if (data.ok) {
        notify('✅ 测试通过已记录', 'success');
        await checkTestPass();
      } else {
        notify(data.msg || '记录失败', 'error');
      }
    } catch (cause) {
      notify('记录失败: ' + (cause instanceof Error ? cause.message : String(cause)), 'error');
    } finally {
      testPass.loading = false;
    }
  }

  /**
   * 1.1.118: 临时静音「顶部全局加载小药丸」——
   *   备货页「保存并重算」用的是**页面正中的加载层** ✓，顶上再飘一个就重复了 ✗
   *   （用户反馈："应该是页面中间会有个圈圈刷新，而不是最顶上有一个"）。
   */
  const networkPillMuted = ref(false);

  return {
    networkPillMuted,
    backendEnv,
    backendStale,
    backendStartedAt,
    backendSourceAt,
    backendSha,
    backendPid,
    sidebarCollapsed,
    version,
    versionLabel,
    toasts,
    testPass,
    loadVersion,
    toggleSidebar,
    notify,
    dismiss,
    checkTestPass,
    markTestPass,
    filterTemplates,
    activeFilterTemplateId,
    activeFilterTemplate,
    filterApplying,
    loadFilterTemplates,
    setFilterTemplate,
    consumeFilterToast,
  };
});
